import { useCallback, useRef } from 'react'

import { EuiProgress, EuiSpacer } from '@equipmentshare/ds2'
import { heapEvent } from '@equipmentshare/heaper'

import Branch from '../Branch'
import { BranchWithInventoryLocations, InventoryLocationWithLabel } from 'hooks/inventory-location-search'
import usePermissions from 'hooks/usePermissions'
import { Permission } from '../../../../../graphql'
import {
	getEditInventoryLocationModal,
	getSetDefaultInventoryLocationModal,
	getDeleteInventoryLocationModal,
	getMoveInventoryLocationModal,
	getWarehouseSettingsModal,
} from '../modals'
import { useDispatchSetSelectedModal } from 'contexts/modal-context/modal-context'
import { InventoryLocation } from 'inventory-web'
import useInventoryLocationIsEmpty from '../../hooks/useInventoryLocationIsEmpty'
import useArchiveInventoryLocationHandler from '../../effects/archiveInventoryLocation'

import styles from '../../inventoryLocations.module.css'

export type BranchListProps = {
	branches: BranchWithInventoryLocations[],
	updateAfterEdit: ( updatedInventoryLocation: InventoryLocation ) => void,
	updateAfterDelete: ( removedId: string, branchId: number ) => void,
	updateAfterMove: ( oldInventoryLocationId: string, movedInventoryLocation: InventoryLocation, fromBranchId: number, toBranchId: number ) => void,
	isLoading: boolean,
	expandedIds: number[],
	onToggleExpandedById: ( id: number ) => void,
	setIsDeletingStore: ( isDeletingStore: boolean ) => void,
}

export default function BranchList( { branches, updateAfterEdit, updateAfterDelete, updateAfterMove, isLoading, expandedIds, onToggleExpandedById, setIsDeletingStore }: BranchListProps ) {
	const setSelectedModal = useDispatchSetSelectedModal()
	// These are the only permissions Actions (rendered per-row below) checks.
	const { userPermissions } = usePermissions( [
		Permission.CanEditStoreByStoreId,
		Permission.CanCreateTopLevelStore,
		Permission.CanArchiveStoreByStoreId,
	] )
	const getIsEmpty = useInventoryLocationIsEmpty()
	const archiveLocation = useArchiveInventoryLocationHandler()

	// Several of the values these handlers close over come from hooks that return a
	// brand-new function on every render (setSelectedModal, getIsEmpty, archiveLocation)
	// or from props. Stash the latest versions in a ref so the row handlers below can
	// keep stable (empty-dependency) identities — that is what lets React.memo on
	// Branch / InventoryLocation / Actions actually skip re-rendering unchanged rows.
	const latest = useRef( { setSelectedModal, getIsEmpty, archiveLocation, updateAfterEdit, updateAfterDelete, updateAfterMove, setIsDeletingStore } )
	latest.current = { setSelectedModal, getIsEmpty, archiveLocation, updateAfterEdit, updateAfterDelete, updateAfterMove, setIsDeletingStore }

	const onEditLocation = useCallback( ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => {
		e.stopPropagation()
		heapEvent( 'Inventory - Stores - Click - Edit', {} )
		latest.current.setSelectedModal( getEditInventoryLocationModal( inventoryLocation, latest.current.updateAfterEdit ) )
	}, [] )

	const onSetDefault = useCallback( ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => {
		e.stopPropagation()
		heapEvent( 'Inventory - Stores - Click - Set as default', {} )
		latest.current.setSelectedModal( getSetDefaultInventoryLocationModal( inventoryLocation, latest.current.updateAfterEdit ) )
	}, [] )

	const onDeleteLocation = useCallback( ( inventoryLocation: InventoryLocationWithLabel ) => async( e: React.MouseEvent ) => {
		latest.current.setIsDeletingStore( true )
		heapEvent( 'Inventory - Stores - Click - Delete', {} )
		e.stopPropagation()

		try {
			const isEmpty = await latest.current.getIsEmpty( inventoryLocation )

			if ( isEmpty ) {
				await latest.current.archiveLocation( inventoryLocation.id )
				latest.current.updateAfterDelete( inventoryLocation.id, inventoryLocation.branch.id )
				latest.current.setIsDeletingStore( false )
			} else {
				latest.current.setSelectedModal( getDeleteInventoryLocationModal() )
				latest.current.setIsDeletingStore( false )
			}
		} catch ( err: unknown ) {
			latest.current.setIsDeletingStore( false )
		}
	}, [] )

	const onMoveLocation = useCallback( ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => {
		e.stopPropagation()
		heapEvent( 'Inventory - Stores - Click - Move to branch', {} )
		latest.current.setSelectedModal( getMoveInventoryLocationModal( inventoryLocation, latest.current.updateAfterMove ) )
	}, [] )

	const onWarehouseSettings = useCallback( ( inventoryLocation: InventoryLocationWithLabel ) => ( e: React.MouseEvent ) => {
		e.stopPropagation()
		heapEvent( 'Inventory - Stores - Click - Warehouse settings', {} )
		latest.current.setSelectedModal( getWarehouseSettingsModal( inventoryLocation, latest.current.updateAfterEdit ) )
	}, [] )

	return (
		<>
			{ isLoading && <EuiProgress size="xs" /> }
			{ branches.map( ( branch, index ) => (
				<div key={branch.id} className={styles.storeContainer}>
					<EuiSpacer size="s" />
					<Branch
						branch={branch}
						userPermissions={userPermissions}
						onEditLocation={onEditLocation}
						onSetDefault={onSetDefault}
						onDeleteLocation={onDeleteLocation}
						onMoveLocation={onMoveLocation}
						onWarehouseSettings={onWarehouseSettings}
						isExpanded={expandedIds.includes( branch.id )}
						onToggleExpanded={onToggleExpandedById}
					/>
					{ ( index !== branches.length - 1 ) ? <EuiSpacer size="s" /> : undefined}
				</div>
			) ) }
		</>
	)
}
